/**
 * GifEstimateStore — per-file natural-GIF-size estimates feeding the v5
 * "Max file size" verdict notes (docs: ironman/docs/GIF_EXACT_SIZE_DESIGN.md §6,
 * "Estimator"). Three fidelity stages, each superseding the last:
 *
 *   Stage 0 (client, at file selection): <video> loadedmetadata on an object
 *     URL — attempt-based runtime feature detection (browsers can't demux
 *     avi/wmv-class containers, 0.26% of prod traffic; those wait for Stage 1).
 *   Stage 1 (server quick, when the /files/input/info ffprobe basics land):
 *     pixel-frames estimate of the OUTPUT at default settings — source
 *     resolution at min(source fps, 10 fps gif-output default).
 *   Stage 2 (server sample-refined, post-upload /files/source/probe):
 *     measured ~1s gifski sample encodes -> approxSize + achievable floorSize.
 *
 * compress-gif pages: the natural size IS the current file size — recorded
 * synchronously via recordKnownNaturalSize, no probes.
 *
 * Estimates are ADVISORY (notes/verdicts only, keyed by fileUpload.id); they
 * never change a chosen option value. Lifecycle guards (binding): single-flight
 * probe per file id, stale resolutions dropped against the tracked set,
 * cancellation on unmount/file-removal/page-nav, no leaked timers or promises.
 *
 * This module is deliberately LEAF-LIKE: no service imports (the 11-service
 * inversify SCC TDZ-crashes the SSG build on careless imports). The Stage-2
 * transport is injected as a callback by the wiring component.
 */

/**
 * Bytes per pixel-frame of a default-settings GIF encode.
 * MUST match ironman GifProbeEstimator.BYTES_PER_PIXEL_FRAME — the backend twin
 * derived 2026-08-23 from the prod mp4->gif corpus (video-results.ndjson,
 * median initialGifBytes/frames = 74,516 B at 480x270 => 0.575 B/pixel-frame;
 * full derivation + assumptions in the ironman code comment and T3 report).
 * One constant on each side, pinned by tests; update both together.
 */
export const GIF_NATURAL_BYTES_PER_PIXEL_FRAME = 0.575;

/**
 * Default gif-OUTPUT frame rate. The xconfig gif pipelines default to
 * 10 FPS ("10_FRAMERATE (Recommended)", selected) at IMAGE_RESOLUTION_ORIGINAL,
 * so the estimate models the OUTPUT at those defaults: source resolution at
 * min(source fps, 10) — a 24 fps source still renders 10 GIF frames/second,
 * an 8 fps source keeps its 8. When the source fps is unknown (Stage 0
 * loadedmetadata carries none) the output default is assumed outright.
 * NOTE: the estimate assumes the DEFAULT output settings; it does not read the
 * user's current resolution/framerate selections — Stage 2's server probe does
 * (the probe request carries the live options summary) and supersedes this.
 */
export const GIF_OUTPUT_DEFAULT_FPS = 10;

/** Output fps at default settings: the source rate, capped at the 10 fps default. */
export const gifOutputFps = (sourceFps: number | undefined): number =>
    typeof sourceFps === 'number' && isFinite(sourceFps) && sourceFps > 0
        ? Math.min(sourceFps, GIF_OUTPUT_DEFAULT_FPS)
        : GIF_OUTPUT_DEFAULT_FPS;

/**
 * Container-metadata sanity clamps (metadata can lie — rotated/anamorphic/VFR/
 * truncated files): out-of-band values produce NO estimate and the next stage
 * decides. Values mirror ironman GifProbeEstimator.
 */
export const GIF_ESTIMATE_SANITY = {
    MAX_DURATION_SECONDS: 4 * 3600,
    MAX_DIMENSION: 8192,
    MAX_FPS: 120,
};

/** How long Stage 0 waits for <video> loadedmetadata before giving up. */
export const GIF_STAGE0_METADATA_TIMEOUT_MS = 2000;

/**
 * Source extensions treated as video for GIF-output estimation. Single source
 * of truth — the per-file verdict notes use the same list.
 */
export const GIF_SOURCE_VIDEO_EXTENSIONS = [
    'mp4', 'mov', 'webm', 'mkv', 'avi', 'wmv', 'm4v', 'mpg', 'mpeg', '3gp', 'flv', 'ts', 'mts',
];

export type GifEstimateSourceKind = 'gif' | 'video' | 'other';

/** Classify a filename for GIF-output estimation purposes. */
export const gifEstimateSourceKind = (fileName: string | undefined | null): GifEstimateSourceKind => {
    const ext = ((fileName || '').split('.').pop() || '').toLowerCase();
    if (ext === 'gif') return 'gif';
    if (GIF_SOURCE_VIDEO_EXTENSIONS.includes(ext)) return 'video';
    return 'other';
};

/** Natural-GIF-size estimate from geometry + frame count. */
export const gifEstimateFromFrames = (width: number, height: number, frames: number): number =>
    Math.round(width * height * frames * GIF_NATURAL_BYTES_PER_PIXEL_FRAME);

/** Natural-GIF-size estimate from geometry + fps + duration (seconds). */
export const naturalGifEstimateBytes = (
    width: number, height: number, fps: number, durationSeconds: number,
): number => gifEstimateFromFrames(width, height, fps * durationSeconds);

const saneDimension = (v: number | undefined): v is number =>
    typeof v === 'number' && isFinite(v) && v > 0 && v <= GIF_ESTIMATE_SANITY.MAX_DIMENSION;
const saneDuration = (v: number | undefined): v is number =>
    typeof v === 'number' && isFinite(v) && v > 0 && v <= GIF_ESTIMATE_SANITY.MAX_DURATION_SECONDS;

export interface GifEstimateRecord {
    /** Highest fidelity stage that has landed for this file. */
    stage: 0 | 1 | 2;
    /** Pixel-frames natural-GIF estimate (Stage 0/1, or the server's quick section). */
    naturalGifEstimate?: number;
    /** Stage-2 measured sample-encode extrapolation — supersedes the pixel-frames model. */
    approxSize?: number;
    /** Stage-2 achievable floor at solver-floor settings. */
    floorSize?: number;
    /** True when the Stage-2 probe could only compute the quick section. */
    quickOnly?: boolean;
}

/** Best available natural-size estimate: measured bytes supersede the model. */
export const gifEstimatedNaturalBytes = (record: GifEstimateRecord | undefined): number | undefined =>
    record ? (record.approxSize ?? record.naturalGifEstimate) : undefined;

/** Structural subset of InputFileInfo (types/file-upload.types) Stage 1 reads. */
export interface GifEstimateInputInfo {
    duration?: number;
    width?: number;
    height?: number;
    framerate?: number;
    numberOfFrames?: number;
}

/** Structural subset of the /files/source/probe gif-branch response (T3).
 *  -1 marks unavailable values, mirroring the backend. */
export interface GifSourceProbeData {
    naturalGifEstimate?: number;
    approxSize?: number;
    floorSize?: number;
    quickOnly?: boolean;
    error?: boolean;
}

type Listener = () => void;

const positiveOrUndefined = (v: number | undefined): number | undefined =>
    typeof v === 'number' && isFinite(v) && v > 0 ? v : undefined;

export class GifEstimateStore {
    private records = new Map<string, GifEstimateRecord>();
    private tracked = new Set<string>();
    private listeners = new Set<Listener>();
    /** Stage-2 single-flight + once-per-file (a failed probe is not retried). */
    private probeAttempted = new Set<string>();
    /** Pending Stage-0 attempts, cancellable on removal. */
    private stage0Cancels = new Map<string, () => void>();

    subscribe(listener: Listener): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    get(id: string): GifEstimateRecord | undefined {
        return this.records.get(id);
    }

    isTracked(id: string): boolean {
        return this.tracked.has(id);
    }

    /** Mark a file id live. Idempotent; estimates only apply to tracked ids. */
    track(id: string) {
        this.tracked.add(id);
    }

    /**
     * File removed / component unmounted / page navigated away: cancel the
     * Stage-0 attempt, drop the record, and mark any in-flight probe
     * resolution stale (it will be discarded on arrival).
     */
    remove(id: string) {
        const cancel = this.stage0Cancels.get(id);
        if (cancel) cancel();
        this.tracked.delete(id);
        if (this.records.delete(id)) {
            this.notify();
        }
    }

    /** Test/support seam: full reset. */
    clear() {
        Array.from(this.stage0Cancels.values()).forEach(cancel => cancel());
        this.stage0Cancels.clear();
        this.records.clear();
        this.tracked.clear();
        this.probeAttempted.clear();
    }

    /**
     * Stage 0 — attempt-based client estimate at file selection. Failure (non-File
     * source, undemuxable container, timeout) simply means "wait for Stage 1".
     */
    beginClientEstimate(id: string, file: unknown) {
        this.track(id);
        if (this.records.has(id) || this.stage0Cancels.has(id)) return;
        if (typeof document === 'undefined' || typeof URL === 'undefined' || !URL.createObjectURL) return;
        if (typeof File === 'undefined' || !(file instanceof File)) return;

        let url: string;
        try {
            url = URL.createObjectURL(file);
        } catch (e) {
            return;
        }
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.muted = true;

        let finished = false;
        const cleanup = () => {
            if (finished) return;
            finished = true;
            clearTimeout(timer);
            video.removeEventListener('loadedmetadata', onMetadata);
            video.removeEventListener('error', cleanup);
            try {
                video.removeAttribute('src');
                video.load();
            } catch (e) { /* jsdom / detached element */ }
            URL.revokeObjectURL(url);
            this.stage0Cancels.delete(id);
        };
        const onMetadata = () => {
            const width = video.videoWidth;
            const height = video.videoHeight;
            const duration = video.duration;
            cleanup();
            if (saneDimension(width) && saneDimension(height) && saneDuration(duration)) {
                // loadedmetadata exposes no fps — assume the 10 fps output default.
                this.apply(id, 0, {
                    naturalGifEstimate: naturalGifEstimateBytes(width, height, GIF_OUTPUT_DEFAULT_FPS, duration),
                });
            }
        };
        const timer = setTimeout(cleanup, GIF_STAGE0_METADATA_TIMEOUT_MS);
        video.addEventListener('loadedmetadata', onMetadata);
        video.addEventListener('error', cleanup);
        this.stage0Cancels.set(id, cleanup);
        video.src = url;
    }

    /**
     * Stage 1 — server ffprobe basics landed (the EXISTING /files/input/info
     * fetch; this method piggybacks on its result, no extra HTTP call).
     *
     * Models the OUTPUT at default settings: source resolution at
     * min(source fps, GIF_OUTPUT_DEFAULT_FPS). The raw source frame count /
     * source fps must NOT drive the estimate directly — a 30 fps source renders
     * only 10 GIF frames/second at the default, so raw frames would 3x it.
     */
    recordInputFileInfo(id: string, info: GifEstimateInputInfo | undefined | null) {
        if (!info) return;
        const { width, height, framerate, numberOfFrames, duration } = info;
        if (!saneDimension(width) || !saneDimension(height)) return;
        if (typeof framerate === 'number' && framerate > GIF_ESTIMATE_SANITY.MAX_FPS) {
            return; // metadata lies (VFR screen recordings etc.) — Stage 2 decides
        }
        const sourceFps = typeof framerate === 'number' && isFinite(framerate) && framerate > 0
            ? framerate
            : undefined;
        const sourceFrames = typeof numberOfFrames === 'number' && isFinite(numberOfFrames) && numberOfFrames > 0
            ? numberOfFrames
            : undefined;
        // Duration anchors the output frame count; derive it from the real
        // frame count when the container omits it.
        let durationSeconds = saneDuration(duration) ? duration : undefined;
        if (durationSeconds === undefined && sourceFrames !== undefined && sourceFps !== undefined) {
            const derived = sourceFrames / sourceFps;
            durationSeconds = saneDuration(derived) ? derived : undefined;
        }
        if (durationSeconds === undefined) return;
        const effectiveSourceFps = sourceFps ?? (sourceFrames !== undefined ? sourceFrames / durationSeconds : undefined);
        const outputFrames = gifOutputFps(effectiveSourceFps) * durationSeconds;
        this.apply(id, 1, { naturalGifEstimate: gifEstimateFromFrames(width, height, outputFrames) });
    }

    /**
     * compress-gif pages: the natural size is the file's current size —
     * recorded at full fidelity, nothing to refine.
     */
    recordKnownNaturalSize(id: string, bytes: number) {
        this.track(id);
        if (typeof bytes === 'number' && isFinite(bytes) && bytes > 0) {
            this.apply(id, 2, { naturalGifEstimate: Math.round(bytes), quickOnly: false });
        }
    }

    /**
     * Stage 2 — sample-refined server probe, once per file id (single-flight;
     * a failure is not retried). The transport callback is provided by the
     * wiring component (keeps this module service-free). Stale resolutions —
     * the file was removed while the probe ran — are dropped.
     */
    async runServerProbe(id: string, transport: () => Promise<GifSourceProbeData>): Promise<void> {
        this.track(id);
        if (this.probeAttempted.has(id)) return;
        this.probeAttempted.add(id);
        let data: GifSourceProbeData;
        try {
            data = await transport();
        } catch (e) {
            return; // advisory only — degrade silently, Stage 0/1 estimate stands
        }
        if (!this.tracked.has(id)) return; // stale: file removed mid-flight
        if (!data || data.error) return;
        this.apply(id, 2, {
            naturalGifEstimate: positiveOrUndefined(data.naturalGifEstimate),
            approxSize: positiveOrUndefined(data.approxSize),
            floorSize: positiveOrUndefined(data.floorSize),
            quickOnly: !!data.quickOnly,
        });
    }

    /**
     * Merge a stage result. Later stages supersede earlier ones; an earlier
     * stage landing late (races) never downgrades. Only defined values
     * overwrite, so a quick-only Stage 2 keeps the Stage-1 estimate.
     */
    private apply(id: string, stage: 0 | 1 | 2, patch: Partial<GifEstimateRecord>) {
        if (!this.tracked.has(id)) return;
        const existing = this.records.get(id);
        if (existing && existing.stage > stage) return;
        const next: GifEstimateRecord = { ...(existing ?? {}), stage } as GifEstimateRecord;
        (Object.keys(patch) as (keyof GifEstimateRecord)[]).forEach(key => {
            const value = patch[key];
            if (value !== undefined) {
                (next as any)[key] = value;
            }
        });
        this.records.set(id, next);
        this.notify();
    }

    private notify() {
        this.listeners.forEach(listener => {
            try {
                listener();
            } catch (e) {
                console.error(e);
            }
        });
    }
}

/** App-wide singleton (estimates are page-scoped by fileUpload.id lifecycle). */
export const gifEstimateStore = new GifEstimateStore();
